import {
  mapMaintenanceTypeToColor,
  mapMaintenanceTypeToIcon,
} from '../../screens/localities/locality-dashboard/InformationCards/LocalityMaintenanceCard'
import { Box, Chip, styled, Typography, useMediaQuery, useTheme } from '@mui/material'
import { useTranslation } from 'react-i18next'
import { IMaintenance } from '../../generated-types/IMaintenance'
import { getLocalDateTime } from '../../utils/function'
import { DialogContextType, MaintenanceType } from '../../enums'
import { AppRoutesNavigation } from '../../app-routes'
import { useNavigation } from 'src/context/NavigationContext'
import { useDialogCtx, useLocationCtx } from 'src/hooks/context-hooks'
import { permissionValue } from 'src/enums/permissionValue'
import { hasPermission } from '../common/Authorization'
import moment from 'moment'
import { DailyMaintenancesDialog } from 'src/screens/localities/maintenance/DailyMaintenancesDialog'
import { useCallback } from 'react'
import { FlexSpinner } from '../Spinner'
import { formatNumber } from '../../utils/function'
import { getTranslatedText } from '../../utils/translations'
import { IPaginatedResult } from 'src/api/paginated-result'
import { GetRGBA } from '../../utils'

const translateConnectedEntityName = (name?: string) => {
  if (!name) return undefined
  return name
    .split(' - ')
    .map(part => {
      if (part.startsWith('{')) {
        try {
          return getTranslatedText(part)
        } catch {
          return part
        }
      }
      return part
    })
    .filter(Boolean)
    .join(' - ')
}

export const RowMaintenanceTypeImageBox = ({ type }) => {
  const IconComponent = mapMaintenanceTypeToIcon(type)

  return (
    <Box
      bgcolor={GetRGBA(mapMaintenanceTypeToColor(type), 0.8)}
      width="24px"
      height="24px"
      borderRadius="3px"
      display="flex"
      justifyContent="center"
      alignItems="center"
    >
      {IconComponent && <IconComponent sx={{ fontSize: 16, color: 'common.white' }} />}
    </Box>
  )
}

const MAINTENANCE_TYPE_LABEL: Record<number, { key: string; ns: string }> = {
  [MaintenanceType.Deviation]: { key: 'deviations', ns: 'common' },
  [MaintenanceType.Service]: { key: 'services', ns: 'common' },
  [MaintenanceType.Task]: { key: 'tasks', ns: 'common' },
  [MaintenanceType.WorkAssignment]: { key: 'assignment', ns: 'common' },
  [MaintenanceType.DailyMaintenance]: { key: 'daily-maintenance', ns: 'maintenance' },
}

const getTypeLabel = (type: MaintenanceType, t: (key: string, opts?: any) => string) => {
  const entry = MAINTENANCE_TYPE_LABEL[type]
  if (!entry) return t('maintenance', { ns: 'maintenance' })
  return t(entry.key, { ns: entry.ns })
}

interface ILocalityMaintenanceGrid {
  customerId?: string
  localityId?: string
  maintenanceType?: MaintenanceType
  maintenanceForWeek?: boolean
  totalCount?: number
  dailyMaintenanceCount?: number
  maintenancesData?: IPaginatedResult<IMaintenance>
  maintenancesLoading?: boolean
}

export const LocalityMaintenanceGrid = ({
  customerId,
  localityId,
  maintenanceType,
  totalCount,
  dailyMaintenanceCount,
  maintenancesData,
  maintenancesLoading,
}: ILocalityMaintenanceGrid) => {
  const { t } = useTranslation(['common', 'maintenance'])
  const theme = useTheme()
  const isMobile = useMediaQuery(theme.breakpoints.down('sm'))
  const { navigateTo } = useNavigation()
  const { permission } = useLocationCtx()
  const { showDialog } = useDialogCtx()
  const data = maintenancesData
  const isLoading = maintenancesLoading ?? false
  const DailyMaintenanceDetails = {
    id: 0,
    title: t('daily-maintenance', { ns: 'maintenance' }),
    entityType: MaintenanceType.DailyMaintenance,
    dailyMaintenanceCount: dailyMaintenanceCount,
  } as IMaintenance

  const dataSet = maintenanceType !== undefined ? data?.items : [DailyMaintenanceDetails, ...(data?.items ?? [])]

  const navigateToMaintenance = useCallback(
    (item: IMaintenance) => {
      if (
        item.entityType === MaintenanceType.Deviation &&
        hasPermission([permissionValue.Locality.Deviation.Read], permission)
      ) {
        navigateTo(
          AppRoutesNavigation.Localities.deviation(customerId as EntityId, localityId as EntityId, item.entityId),
        )
      } else if (
        item.entityType === MaintenanceType.Service &&
        hasPermission([permissionValue.Locality.Service.Read], permission)
      ) {
        navigateTo(
          AppRoutesNavigation.Localities.localityService(customerId as EntityId, localityId as EntityId, item.entityId),
        )
      } else if (
        item.entityType === MaintenanceType.Task &&
        hasPermission([permissionValue.Locality.Task.Read], permission)
      ) {
        navigateTo(
          AppRoutesNavigation.Localities.workOrder(customerId as EntityId, localityId as EntityId, item.entityId),
        )
      } else if (
        item.entityType === MaintenanceType.WorkAssignment &&
        hasPermission([permissionValue.Locality.WorkAssignment.Read], permission)
      ) {
        navigateTo(
          AppRoutesNavigation.Localities.workAssignment(customerId as EntityId, localityId as EntityId, item.entityId),
        )
      }
    },
    [navigateTo, customerId, localityId, permission],
  )

  const handleRowClick = useCallback(
    (item: IMaintenance) => {
      if (item.entityType === MaintenanceType.DailyMaintenance) {
        showDialog(
          DailyMaintenancesDialog,
          {
            opts: { maxWidth: false, fullWidth: true, rootWidth: '680px' },
          },
          DialogContextType.localityContext,
          true,
        )
        return
      }
      navigateToMaintenance(item)
    },
    [showDialog, navigateToMaintenance],
  )

  const getTypeColor = (type: MaintenanceType) => {
    if (type === MaintenanceType.Deviation) return theme.palette.error.main
    return theme.palette.primary.main
  }

  const formatDueDate = (dueDate: any) => {
    if (!dueDate) return null
    const date = moment(getLocalDateTime(dueDate))
    const today = moment().startOf('day')
    const diff = date.diff(today, 'days')
    if (diff < 0) return { text: date.format('YYYY-MM-DD'), color: theme.palette.error.main, overdue: true }
    if (diff === 0) return { text: t('today', { ns: 'common' }), color: theme.palette.primary.main, overdue: false }
    return { text: date.format('YYYY-MM-DD'), color: theme.palette.text.secondary, overdue: false }
  }

  if (isLoading) {
    return (
      <Box data-test-id="maintenance-loading" sx={{ py: 4 }}>
        <FlexSpinner />
      </Box>
    )
  }

  if (!dataSet || dataSet.length === 0) {
    return (
      <Box sx={{ py: 4, textAlign: 'center' }}>
        <Typography variant="body2" color="text.secondary">
          {t('no-data', { ns: 'common' })}
        </Typography>
      </Box>
    )
  }

  return (
    <ListContainer data-test-id="maintenance-list-container">
      {dataSet.map((item, idx) => {
        const isDailyMaintenance = item.entityType === MaintenanceType.DailyMaintenance
        const dueInfo = !isDailyMaintenance ? formatDueDate(item.dueDate) : null

        return (
          <RowContainer
            key={item.id || `dm-${idx}`}
            onClick={() => handleRowClick(item)}
            data-test-id={isDailyMaintenance ? 'maintenance-daily-row' : 'maintenance-row'}
          >
            {/* Type indicator */}
            <Box sx={{ flexShrink: 0, mr: isMobile ? 1 : 1.5 }}>
              <RowMaintenanceTypeImageBox type={item.entityType} />
            </Box>

            {/* Content */}
            <Box sx={{ flex: 1, minWidth: 0 }}>
              <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.75 }}>
                <Typography
                  variant="body2"
                  fontWeight={600}
                  noWrap
                  sx={{ flex: 1, minWidth: 0, fontSize: isMobile ? '0.8rem' : undefined }}
                >
                  {item.title}
                </Typography>
                {!isDailyMaintenance && dueInfo && (
                  <Typography
                    variant="caption"
                    fontWeight={dueInfo.overdue ? 600 : 400}
                    noWrap
                    sx={{ color: dueInfo.color, flexShrink: 0, mr: 0.75 }}
                  >
                    {dueInfo.text}
                  </Typography>
                )}
                {isDailyMaintenance ? (
                  <Box
                    sx={{
                      backgroundColor: theme.palette.primary.dark,
                      color: theme.palette.common.white,
                      borderRadius: '4px',
                      minWidth: 24,
                      height: 20,
                      px: 0.75,
                      display: 'flex',
                      justifyContent: 'center',
                      alignItems: 'center',
                      flexShrink: 0,
                    }}
                  >
                    <Typography variant="caption" fontWeight={600} sx={{ fontSize: '0.75rem' }}>
                      {formatNumber(dailyMaintenanceCount)}
                    </Typography>
                  </Box>
                ) : (
                  <Chip
                    label={getTypeLabel(item.entityType, t)}
                    size="small"
                    sx={{
                      height: 18,
                      fontSize: '0.75rem',
                      backgroundColor: `${getTypeColor(item.entityType)}14`,
                      color: getTypeColor(item.entityType),
                      borderColor: `${getTypeColor(item.entityType)}40`,
                      fontWeight: 600,
                      flexShrink: 0,
                      '& .MuiChip-label': { px: 0.75 },
                    }}
                    variant="outlined"
                  />
                )}
              </Box>
              {!isDailyMaintenance && (
                <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.75, mt: '2px' }}>
                  {item.connectedEntityName && (
                    <Typography variant="caption" color="text.secondary" noWrap sx={{ fontSize: '0.75rem' }}>
                      {translateConnectedEntityName(item.connectedEntityName)}
                    </Typography>
                  )}
                  {item.createdBy && item.entityType !== MaintenanceType.Service && (
                    <Typography variant="caption" color="text.secondary" noWrap sx={{ fontSize: '0.75rem' }}>
                      {item.connectedEntityName ? `· ${item.createdBy}` : item.createdBy}
                    </Typography>
                  )}
                </Box>
              )}
            </Box>
          </RowContainer>
        )
      })}
    </ListContainer>
  )
}

const ListContainer = styled(Box)({
  overflowY: 'auto',
})

const RowContainer = styled(Box)(({ theme }) => ({
  display: 'flex',
  alignItems: 'center',
  padding: '8px 12px',
  borderBottom: `1px solid ${theme.palette.divider}`,
  cursor: 'pointer',
  transition: 'background-color 0.15s',
  '&:hover': {
    backgroundColor: theme.palette.action.hover,
  },
  '&:active': {
    backgroundColor: theme.palette.action.selected,
  },
  '&:last-child': {
    borderBottom: 'none',
  },
  [theme.breakpoints.down('sm')]: {
    padding: '10px 8px',
  },
}))
